import React, { useState } from 'react';
import styled from 'styled-components';
import { Popover } from '@material-ui/core';
import ConceptCard from './ConceptCard';
import { getConceptRefLabel } from 'constants/conceptRefs';

interface IProps {
  conceptRef: number;
  refId: number;
  courseId: number;
  showLabel?: boolean;
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
}

const ConceptCardHover: React.FC<IProps> = ({
  conceptRef,
  refId,
  courseId,
  showLabel = true,
  onClick,
  className,
  children,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const enterTimeoutRef = React.useRef<number>();
  const leaveTimeoutRef = React.useRef<number>();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.currentTarget;
    clearTimeout(leaveTimeoutRef.current);
    clearTimeout(enterTimeoutRef.current);

    enterTimeoutRef.current = window.setTimeout(() => {
      setAnchorEl(target);
    }, 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(enterTimeoutRef.current);
    clearTimeout(leaveTimeoutRef.current);

    leaveTimeoutRef.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 300);
  };

  const handlePopoverMouseEnter = () => {
    clearTimeout(leaveTimeoutRef.current);
  };

  const handlePopoverMouseLeave = () => {
    clearTimeout(leaveTimeoutRef.current);

    leaveTimeoutRef.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 300);
  };

  React.useEffect(() => {
    return () => {
      clearTimeout(enterTimeoutRef.current);
      clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  const open = Boolean(anchorEl);
  const label = getConceptRefLabel(conceptRef);

  return (
    <>
      <ConceptLink
        className={className}
        onClick={handleClick}
        clickable={!!onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children || (
          <>
            {showLabel && <ConceptType>{label}</ConceptType>}
            <ConceptId>#{refId}</ConceptId>
          </>
        )}
      </ConceptLink>
      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        disableScrollLock
        hideBackdrop
        style={{ pointerEvents: 'none' }}
        PaperProps={{
          style: { pointerEvents: 'auto' },
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave,
        }}
      >
        <PopoverContent>
          <ConceptCard conceptRef={conceptRef} refId={refId} courseId={courseId} />
        </PopoverContent>
      </StyledPopover>
    </>
  );
};

export default ConceptCardHover;

const StyledPopover = styled(Popover)`
  .MuiPopover-paper {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    max-width: 600px;
    max-height: 400px;
    overflow: auto;
  }
`;

const PopoverContent = styled.div`
  padding: 0;
`;

const ConceptLink = styled.span<{ clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1976d2;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  transition: background-color 0.2s;
  font-size: 14px;

  &:hover {
    background-color: rgba(25, 118, 210, 0.08);
  }

  ${(props) =>
    props.clickable &&
    `
    &:active {
      background-color: rgba(25, 118, 210, 0.12);
    }
  `}
`;

const ConceptType = styled.span`
  font-weight: 500;
`;

const ConceptId = styled.span`
  color: #666;
`;
